// Offer name + form helpers for Create Global Offer

export const MMPS = [
  { value: 'AF', label: 'AppsFlyer' },
  { value: 'AF External', label: 'AppsFlyer Ex' },
  { value: 'Adjust', label: 'Adjust' },
  { value: 'Singular', label: 'Singular' },
  { value: 'Branch', label: 'Branch' },
  { value: 'Kochava', label: 'Kochava' },
  { value: 'Other', label: 'Other' },
  { value: '', label: 'None' },
]

export const PAYABLE_GOALS = ['install', 'registration', 'ftd', 'purchase', 'subscription']

export const KPI_DEFS = [
  { key: 'click_to_install', label: 'Click to Install', group: 'CR', splittable: true },
  { key: 'install_to_reg', label: 'Install to Reg', group: 'CR', splittable: true },
  { key: 'reg_to_cpa', label: 'Reg to CPA', group: 'CR', splittable: true },
  { key: 'ftd_to_qftd', label: 'FTD to QFTD ratio', group: 'CR', splittable: true },
  { key: 'retention_d1', label: 'Retention D1', group: 'Retention Rate', splittable: true },
  { key: 'retention_d7', label: 'Retention D7', group: 'Retention Rate', splittable: true },
  { key: 'retention_d30', label: 'Retention D30', group: 'Retention Rate', splittable: true },
  { key: 'ctr', label: 'CTR', group: null, splittable: true },
  { key: 'max_fraud', label: 'Max Fraud %', group: null, splittable: true },
  { key: 'mtti', label: 'MTTI', group: null, splittable: false },
]

export const TIMEFRAMES = ['daily', 'weekly', 'monthly']
export const CAP_SCOPES = ['Both', 'CTA', 'VTA']
export const CAP_DEFAULT_TIMEFRAME = { click: 'daily', conversion: 'monthly', budget: 'monthly' }

export const PAYMENT_TYPES = [
  { value: 'CPA(fixed)', label: 'CPA' },
  { value: 'CPS(percent)', label: 'RS%' },
]
export const OS_OPTIONS = ['All', 'iOS', 'Android']
export const CURRENCIES = ['USD', 'EUR', 'GBP']
export const ADV_CUSTOM_NUMBERS = [1, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]
// Affise payout device types from GET /3.1/devices
export const AFFISE_DEVICE_TYPES = [
  'mobile',
  'tablet',
  'desktop',
  'tv',
  'console',
  'ereader',
  'mediahub',
  'smartwatch',
]

export const ADD_BUNDLE_URL = '/bundle-ids/edit/0'
export const ADJUST_LINK_GENERATOR_URL = '/man/adjust-link-generator'
export const AF_LINK_GENERATOR_URL = '/man/appsflyer-link-generator'

export function isAppsFlyerMmp(mmp) {
  return mmp === 'AF' || mmp === 'AppsFlyer' || mmp === 'AF External'
}

export function shortAccount(acc) {
  if (!acc) return ''
  return String(acc).split(' (')[0].trim()
}

export function sanitizeCsv(v) {
  return String(v == null ? '' : v).replace(/\s+/g, '')
}

/** Debounce: run fn after waitMs of quiet. Returns cancel(). */
export function debounce(fn, waitMs = 300) {
  let timer = null
  const wrapped = (...args) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn(...args)
    }, waitMs)
  }
  wrapped.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
  return wrapped
}

let _kpiSeq = 0
export function newKpiRow(key) {
  return {
    id: 'kpi_' + ++_kpiSeq + '_' + Math.random().toString(36).slice(2, 6),
    key,
    mode: 'shared',
    value: '',
    valueCTA: '',
    valueVTA: '',
  }
}

export function kpiDef(key) {
  return KPI_DEFS.find((d) => d.key === key)
}

let _capSeq = 0
export function newCap(kind) {
  return {
    id: 'cap_' + ++_capSeq + '_' + Math.random().toString(36).slice(2, 6),
    kind,
    value: '',
    event: '',
    timeframe: CAP_DEFAULT_TIMEFRAME[kind],
    scope: 'Both',
  }
}

let _prSeq = 0
export function newPayoutRow(overrides = {}) {
  return {
    goalValue: '',
    paymentType: 'CPA(fixed)',
    revenue: '',
    payouts: '',
    payoutsEdited: false,
    goalTitle: 'Default',
    country: '',
    os: 'All',
    currency: 'USD',
    cities: [],
    devices: [],
    custom: { '2': '', '3': '' },
    ...overrides,
    id: 'pr_' + ++_prSeq + '_' + Math.random().toString(36).slice(2, 6),
  }
}

/**
 * Offer name: APPNAME ADV MMPBLOCK [FREETEXT] ATTR GEO OS
 * AF -> "AF (agency) (account)"; Adjust -> "Adjust (postback URL)"; else MMP label.
 * VTA attribution expands to two offers per account: ATTR "CTA" (variant CTA)
 * and ATTR "VTA" (variant VTA imp).
 */
export function buildOfferNames({ bundle, selectedAdv, mmp, afAgency, afAccounts, attribution, country, freeText }) {
  const app = (bundle && bundle.app) || ''
  const adv = (selectedAdv && selectedAdv.name) || ''
  const geo = country || ''
  const os = (bundle && bundle.os) || ''
  const ft = (freeText || '').trim()
  // Name ATTR label vs Affise variant: VTA mode creates CTA + VTA-named rows
  const attrVariants =
    attribution === 'VTA'
      ? [
          { attrLabel: 'CTA', variant: 'CTA' },
          { attrLabel: 'VTA', variant: 'VTA imp' },
        ]
      : [{ attrLabel: attribution || 'CTA', variant: 'CTA' }]

  const assemble = (mmpBlock, account, attrLabel, variant) => ({
    account,
    variant,
    name: [app, adv, mmpBlock, ft, attrLabel, geo, os]
      .filter((s) => s && String(s).length)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim(),
  })

  const expand = (mmpBlock, account) =>
    attrVariants.map(({ attrLabel, variant }) => assemble(mmpBlock, account, attrLabel, variant))

  if (isAppsFlyerMmp(mmp)) {
    const agencyPart = afAgency && afAgency !== 'none' && afAgency !== '' ? `(${afAgency})` : ''
    const accounts = afAccounts && afAccounts.length ? afAccounts : [null]
    const out = []
    accounts.forEach((acc) => {
      const accPart = acc ? `(${shortAccount(acc)})` : ''
      const mmpBlock = ['AF', agencyPart, accPart].filter(Boolean).join(' ')
      expand(mmpBlock, acc).forEach((item) => out.push(item))
    })
    return out
  }
  if (mmp === 'Adjust') {
    return expand('Adjust (postback URL)', 'Adjust')
  }
  const mmpLabel = (MMPS.find((m) => m.value === mmp) || {}).label || mmp || ''
  return expand(mmpLabel, null)
}

export function mapPaymentTypeForAffise(paymentType) {
  // Affise API: fixed | percent | mixed | cpc | cpm | first_cpa | first_cps | ...
  // RS% in UI → FTD/FTP (RevShare) → first_cps
  if (paymentType === 'CPS(percent)') return 'first_cps'
  return 'fixed'
}

export function mapPaymentTypeFromAffise(paymentType) {
  if (paymentType === 'first_cps' || paymentType === 'CPS(percent)') return 'CPS(percent)'
  return 'CPA(fixed)'
}

export function payoutsFromApiToFormRows(payouts) {
  const rows = Array.isArray(payouts) ? payouts : []
  if (!rows.length) return [newPayoutRow({ _addN: '' })]
  return rows.map((p) => {
    const custom = { '2': '', '3': '', ...(p.custom_fields || {}) }
    const revenue = String(p.revenue || '')
    const payout = String(p.payout || '')
    return newPayoutRow({
      goalValue: String(p.goal || ''),
      paymentType: mapPaymentTypeFromAffise(p.payment_type),
      revenue,
      payouts: payout,
      payoutsEdited: payout !== '' && payout !== revenue,
      country: String(p.country || ''),
      os: p.os ? String(p.os) : 'All',
      currency: String(p.currency || 'USD'),
      cities: (p.cities || []).map((c) => ({
        id: c.id,
        name: c.name || '',
        region: c.region || '',
      })),
      devices: (p.devices || []).map((d) => String(d || '')).filter(Boolean),
      custom,
      _addN: '',
      affisePaymentId: p.affise_payment_id || '',
    })
  })
}

export function findGeneratedLink(generatedLinks, account, trackingUrl, impressionUrl) {
  const fallback = {
    account: account || '',
    click: trackingUrl || '',
    impression: impressionUrl || '',
  }
  if (!generatedLinks || !generatedLinks.length) return fallback

  if (account) {
    const accShort = shortAccount(account).toLowerCase()
    const hit = generatedLinks.find((l) => {
      const linkAcc = shortAccount(l.account || '').toLowerCase()
      return linkAcc && linkAcc === accShort
    })
    if (hit) {
      return {
        account: hit.account || account || '',
        click: hit.click || trackingUrl || '',
        impression: hit.impression || impressionUrl || '',
      }
    }
  }

  // No account match (e.g. Adjust): merge first available click/impression from generated links
  const clickHit = generatedLinks.find((l) => l && l.click)
  const impressionHit = generatedLinks.find((l) => l && l.impression)
  return {
    account: (clickHit && clickHit.account) || (impressionHit && impressionHit.account) || account || '',
    click: (clickHit && clickHit.click) || trackingUrl || '',
    impression: (impressionHit && impressionHit.impression) || impressionUrl || '',
  }
}

export function fileFlowSessionKey(account, variant) {
  return `${account || ''}::${variant || ''}`
}

export function syncFileFlowSessionFromResults(sessionItems, apiResults) {
  const next = { ...(sessionItems || {}) }
  for (const r of apiResults || []) {
    if (!r.local_id || !r.affise_offer_id) continue
    const key = fileFlowSessionKey(r.account, r.variant)
    next[key] = {
      id: r.local_id,
      affise_offer_id: r.affise_offer_id,
    }
  }
  return next
}

function payoutRowHasAffiseData(row) {
  if (String(row.goalValue || '').trim()) return true
  if (String(row.revenue || '').trim()) return true
  if (String(row.payouts || '').trim()) return true
  if (String(row.country || '').trim()) return true
  if (row.os && row.os !== 'All') return true
  if ((row.cities || []).length) return true
  if ((row.devices || []).length) return true
  return Object.keys(row.custom || {}).some((n) => String(row.custom[n] || '').trim())
}

function payoutRowsForPayload(payoutRows) {
  const rows = payoutRows || []
  if (rows.length <= 1) return rows
  const meaningful = rows.filter((row) => payoutRowHasAffiseData(row))
  return meaningful.length ? meaningful : [rows[0]]
}

function buildPayoutPayloadRow(row, index) {
  const revenueValue = String(row.revenue || '').trim()
  const payoutValue = String(row.payoutsEdited ? row.payouts : row.revenue || '').trim() || revenueValue
  const customFields = {}
  Object.keys(row.custom || {}).forEach((n) => {
    const value = String(row.custom[n] || '').trim()
    if (value) customFields[String(n)] = value
  })
  return {
    payment_index: index,
    payment_type: mapPaymentTypeForAffise(row.paymentType),
    currency: row.currency || 'USD',
    revenue: revenueValue,
    payout: payoutValue,
    goal: String(row.goalValue || '').trim(),
    country: String(row.country || '').trim().toUpperCase(),
    os: row.os && row.os !== 'All' ? String(row.os).trim() : '',
    cities: (row.cities || [])
      .filter((c) => c && c.id != null)
      .map((c) => ({
        id: c.id,
        name: String(c.name || ''),
        region: String(c.region || ''),
      })),
    devices: (row.devices || []).map((d) => String(d || '').trim()).filter(Boolean),
    custom_fields: customFields,
    affise_payment_id: row.affisePaymentId || undefined,
  }
}

export function buildFileFlowPayload({
  selectedAdv,
  advertiserId,
  bundleId,
  bundle,
  country,
  attribution,
  offerType,
  mmp,
  afAgency,
  advDefaultUrl,
  clickCaps,
  payoutRows,
  offerNames,
  generatedLinks,
  trackingUrl,
  impressionUrl,
  sessionItems,
}) {
  const firstClickCap = (clickCaps && clickCaps[0]) || {}
  const common = {
    advertiser_affise_id: String((selectedAdv && selectedAdv.affiseAdvId) || '').trim(),
    advertiser_id: String(advertiserId || '').trim(),
    bundle_id: String(bundleId || '').trim(),
    attribution: String(attribution || '').trim(),
    offer_type: String(offerType || '').trim(),
    mmp: String(mmp || '').trim(),
    agency: afAgency && afAgency !== 'none' ? String(afAgency).trim() : '',
    advertiser_default_url: String(advDefaultUrl || '').trim(),
    click_caps: {
      amount: String(firstClickCap.value || '').trim(),
      time_period: String(firstClickCap.timeframe || '').trim(),
    },
    status: 'active',
    privacy: 'private',
    country: String(country || '').trim(),
    payouts: payoutRowsForPayload(payoutRows).map((row, index) => buildPayoutPayloadRow(row, index)),
  }

  const items = []
  for (const o of offerNames || []) {
    const variant = o.variant || 'CTA'
    const link = findGeneratedLink(generatedLinks, o.account, trackingUrl, impressionUrl)
    const trackingUrlValue =
      variant === 'VTA imp' ? link.impression || impressionUrl : link.click || trackingUrl
    const session = (sessionItems && sessionItems[fileFlowSessionKey(o.account, variant)]) || null
    items.push({
      id: session && session.id ? session.id : undefined,
      affise_offer_id: session && session.affise_offer_id ? session.affise_offer_id : undefined,
      title: o.name,
      tracking_url: String(trackingUrlValue || '').trim(),
      variant,
      account: o.account || '',
    })
  }

  return { common, items }
}

export function mapFileFlowResultsToAffiseDisplay(apiResults) {
  const groups = new Map()
  for (const r of apiResults || []) {
    const key = `${r.account || ''}::${r.title || ''}`
    if (!groups.has(key)) {
      groups.set(key, {
        account: r.account || '',
        name: r.title || '',
        affise: [],
        globalOfferId: null,
        errors: [],
      })
    }
    const g = groups.get(key)
    if (r.affise_offer_id) {
      g.affise.push({ kind: r.variant || 'CTA', id: r.affise_offer_id })
      if (!g.globalOfferId) g.globalOfferId = r.local_id
    } else if (r.error) {
      g.errors.push(`${r.variant || 'offer'}: ${r.error}`)
    }
  }
  return Array.from(groups.values())
}

export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
  } catch (e) {
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    try {
      document.execCommand('copy')
    } catch (err) {}
    document.body.removeChild(ta)
  }
}

export function buildOfferCopyText(results) {
  return results
    .map((r) => {
      const affisePart = (r.affise || []).map((a) => `${a.kind}: Affise #${a.id}`).join(' · ')
      const errPart = (r.errors || []).join('; ')
      return [`Global offer id: ${r.globalOfferId}`, r.name, affisePart, errPart].filter(Boolean).join('\n')
    })
    .join('\n\n')
}
